import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import DeviceAlertingPanel from "../components/DeviceAlertingPanel";
import PushNotificationsCard from "../components/PushNotificationsCard";

/** The deployment's mail identities, so this screen can state how an alert is
 *  actually addressed rather than leaving the recipient field to imply it. */
type MailIdentity = {
  helpdesk_address: string | null;
  alerts_address: string | null;
  noreply_address: string | null;
  sending_direct: boolean;
  receiver_listening: boolean;
};

// Alert + monitoring settings (under Monitoring & Alerts). Global + per-device-
// type alert thresholds, and the role-/kind-aware device-offline policy.

type Threshold = {
  metric: string;
  info_pct: number;
  warn_pct: number;
  critical_pct: number;
  re_alert_hours: number;
  delay_seconds?: number | null;
};

const BINARY_METRICS = new Set(["fan_health", "ups_status", "raid_health", "psu_status"]);
// Timer-style metrics — no numeric threshold; the knob is a delay (seconds) the
// condition must persist before alerting (e.g. "alert after offline N seconds").
const TIMER_METRICS = new Set(["device_offline"]);
// Count-style metric (network offline = failed polls before alerting).
const COUNT_METRICS = new Set(["network_device_status"]);
const NETWORK_STATUS_METRIC = "network_device_status";

const METRIC_LABELS: Record<string, string> = {
  disk_used_pct: "Disk usage %",
  memory_used_pct: "Memory usage %",
  cpu_used_pct: "CPU usage %",
  battery_health_pct: "Battery health %",
  cpu_temp_c: "CPU temp °C",
  chassis_temp_c: "Chassis temp °C",
  ups_health_pct: "UPS battery %",
  disk_io_latency_ms: "Disk I/O latency ms",
  fan_health: "Fan health",
  ups_status: "UPS status",
  raid_health: "RAID health",
  psu_status: "PSU status",
  device_offline: "Device offline",
  certificate_expiry: "Certificate expiry (days)",
  network_device_status: "Network device offline",
};

// Threshold scope picker. Agent roles report the numeric metrics; network kinds
// only carry the per-kind network-offline (failed-polls) threshold.
const THRESHOLD_SCOPES = [
  { v: "global", l: "Global default" },
  { v: "server", l: "Servers" },
  { v: "workstation", l: "Workstations" },
  { v: "unknown", l: "Unknown" },
];
const NETWORK_KIND_SCOPES = [
  { v: "switch", l: "Switches" },
  { v: "router", l: "Routers" },
  { v: "firewall", l: "Firewalls" },
  { v: "access_point", l: "Access points" },
  { v: "printer", l: "Printers" },
  { v: "ups", l: "UPS" },
  { v: "nas", l: "NAS" },
  { v: "other", l: "Other network" },
];
const NETWORK_KINDS = new Set(NETWORK_KIND_SCOPES.map((s) => s.v));

// Offline policy covers ALL device classifications.
const AGENT_OFFLINE = [
  { key: "alert_offline_role_server", label: "Servers" },
  { key: "alert_offline_role_workstation", label: "Workstations" },
  { key: "alert_offline_role_unknown", label: "Unknown / unclassified" },
];
const NET_OFFLINE = [
  { key: "alert_offline_kind_switch", label: "Switches" },
  { key: "alert_offline_kind_router", label: "Routers" },
  { key: "alert_offline_kind_firewall", label: "Firewalls" },
  { key: "alert_offline_kind_access_point", label: "Access points" },
  { key: "alert_offline_kind_printer", label: "Printers" },
  { key: "alert_offline_kind_ups", label: "UPS" },
  { key: "alert_offline_kind_nas", label: "NAS" },
  { key: "alert_offline_kind_other", label: "Other" },
];

function Num({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="text-xs">
      <span className="block text-gray-500 dark:text-gray-400 mb-0.5">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-24 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-900"
      />
    </label>
  );
}

export default function MonitoringSettings() {
  const [globalMap, setGlobalMap] = useState<Record<string, Threshold>>({});
  const [order, setOrder] = useState<string[]>([]);
  const [scope, setScope] = useState("global");
  const [drafts, setDrafts] = useState<Record<string, Threshold>>({});
  const [overrideSet, setOverrideSet] = useState<Set<string>>(new Set());
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [mailIdent, setMailIdent] = useState<MailIdentity | null>(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [savingMetric, setSavingMetric] = useState<string | null>(null);
  // Customer scope ("" = all customers / global) for per-customer-per-type thresholds.
  const [scopeCustomer, setScopeCustomer] = useState("");
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  // Per-device overrides section.
  const [devices, setDevices] = useState<{ id: number; hostname: string }[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<number | null>(null);

  const flash = (m: string) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 2000);
  };

  // Resolve the list + per-metric URLs for the current (customer, type) scope.
  const scopeUrls = (cust: string, type: string) => {
    const base = "/api/v1/alerts/thresholds";
    if (!cust && type === "global")
      return { list: base, item: (m: string) => `${base}/${m}`, resettable: false };
    if (!cust)
      return {
        list: `${base}/types/${type}`,
        item: (m: string) => `${base}/types/${type}/${m}`,
        resettable: true,
      };
    if (type === "global")
      return {
        list: `${base}/customers/${cust}`,
        item: (m: string) => `${base}/customers/${cust}/${m}`,
        resettable: true,
      };
    return {
      list: `${base}/customers/${cust}/types/${type}`,
      item: (m: string) => `${base}/customers/${cust}/types/${type}/${m}`,
      resettable: true,
    };
  };

  // Load global thresholds (the base + metric order); returns the map.
  const loadGlobal = useCallback(async () => {
    const r = await api.get("/api/v1/alerts/thresholds");
    const map: Record<string, Threshold> = {};
    const ord: string[] = [];
    (r.data || []).forEach((t: Threshold) => {
      map[t.metric] = { ...t };
      ord.push(t.metric);
    });
    setGlobalMap(map);
    setOrder(ord);
    return map;
  }, []);

  // Build editable drafts for the (customer, type) scope: scope-specific rows
  // layered over the global base (so unset metrics show the inherited value).
  const loadScope = useCallback(
    async (cust: string, type: string, base: Record<string, Threshold>) => {
      const { list, resettable } = scopeUrls(cust, type);
      if (!resettable) {
        setDrafts(Object.fromEntries(Object.entries(base).map(([k, v]) => [k, { ...v }])));
        setOverrideSet(new Set());
        return;
      }
      const r = await api.get(list);
      const ov: Record<string, Threshold> = {};
      (r.data || []).forEach((t: Threshold) => (ov[t.metric] = { ...t }));
      const d: Record<string, Threshold> = {};
      Object.keys(base).forEach((m) => (d[m] = { ...(ov[m] || base[m]) }));
      setDrafts(d);
      setOverrideSet(new Set(Object.keys(ov)));
    },
    [],
  );

  useEffect(() => {
    loadGlobal()
      .then((map) => loadScope("", "global", map))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
    api
      .get("/api/v1/settings")
      .then((r) => setSettings(r.data || {}))
      .catch(() => {});
    // Admin-only; a technician viewing this screen just doesn't get the
    // addressing detail rather than seeing an error.
    api
      .get<MailIdentity>("/api/v1/email-intake/identity")
      .then((r) => setMailIdent(r.data))
      .catch(() => setMailIdent(null));
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers((r.data || []).map((c: any) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
    api
      .get("/api/v1/devices")
      .then((r) =>
        setDevices(
          (r.data || [])
            .map((d: any) => ({ id: d.id, hostname: d.hostname }))
            .sort((a: any, b: any) => a.hostname.localeCompare(b.hostname)),
        ),
      )
      .catch(() => {});
  }, [loadGlobal, loadScope]);

  const reloadScope = async (cust: string, type: string) => {
    setErr("");
    try {
      await loadScope(cust, type, globalMap);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const patch = (metric: string, field: keyof Threshold, v: number) =>
    setDrafts((d) => ({ ...d, [metric]: { ...d[metric], [field]: v } }));

  const saveThreshold = async (metric: string) => {
    const d = drafts[metric];
    if (!d) return;
    setSavingMetric(metric);
    setErr("");
    try {
      const body: Record<string, number> = {
        info_pct: d.info_pct,
        warn_pct: d.warn_pct,
        critical_pct: d.critical_pct,
        re_alert_hours: d.re_alert_hours,
      };
      if (TIMER_METRICS.has(metric) || COUNT_METRICS.has(metric))
        body.delay_seconds = Number(d.delay_seconds ?? 0);
      await api.put(scopeUrls(scopeCustomer, scope).item(metric), body);
      flash(`Saved ${METRIC_LABELS[metric] || metric}`);
      const map = await loadGlobal();
      await loadScope(scopeCustomer, scope, map);
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingMetric(null);
    }
  };

  const resetThreshold = async (metric: string) => {
    setSavingMetric(metric);
    setErr("");
    try {
      await api.delete(scopeUrls(scopeCustomer, scope).item(metric));
      flash("Reset to inherited");
      await loadScope(scopeCustomer, scope, globalMap);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSavingMetric(null);
    }
  };

  const setSetting = async (key: string, value: string) => {
    setSettings((s) => ({ ...s, [key]: value }));
    try {
      await api.put(`/api/v1/settings/${key}`, { value });
      flash("Saved");
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const isOn = (key: string) => (settings[key] ?? "").toString().toLowerCase() === "true";
  const masterOff = !isOn("alert_on_device_offline");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Alerting Settings
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Alert thresholds (global or per device type), how alerts reach you, and the
          device-offline policy. Per-device overrides live on each device's Alerting &amp;
          Maintenance tab.
        </p>
      </div>

      {/* Browser/desktop push. Moved here from Security & MFA: push is a
          DELIVERY CHANNEL for alerts, so it belongs beside the alert recipient
          and thresholds rather than next to authenticator enrolment. */}
      <PushNotificationsCard />

      {err && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}
      {msg && (
        <div className="bg-green-50 dark:bg-green-900/40 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 px-4 py-2 rounded-lg text-sm">
          {msg}
        </div>
      )}

      {/* Offline policy */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Device-offline alerting
        </h2>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isOn("alert_on_device_offline")}
            onChange={(e) =>
              setSetting("alert_on_device_offline", e.target.checked ? "true" : "false")
            }
          />
          <span>Master switch — alert when a device goes offline</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Agent endpoints</p>
            {AGENT_OFFLINE.map((r) => (
              <label key={r.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={masterOff}
                  checked={isOn(r.key)}
                  onChange={(e) => setSetting(r.key, e.target.checked ? "true" : "false")}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Network devices</p>
            {NET_OFFLINE.map((r) => (
              <label key={r.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={masterOff}
                  checked={isOn(r.key)}
                  onChange={(e) => setSetting(r.key, e.target.checked ? "true" : "false")}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 space-y-2">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Alert email</h2>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isOn("alert_emails_enabled")}
            onChange={(e) =>
              setSetting("alert_emails_enabled", e.target.checked ? "true" : "false")
            }
          />
          <span>
            Send alert emails
            <span className="text-xs text-gray-400 ml-1">
              (master on/off — turn off to immediately stop all alert emails)
            </span>
          </span>
        </label>
        {/* Delivery style. The complaint this answers is "still getting a shit
            ton of email alerts everyday" — per-event mail on a fleet of 272
            devices is hundreds of messages a day. */}
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-1">
          <div className="text-sm text-gray-800 dark:text-gray-200 mb-1">How alerts are emailed</div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={settings["alert_email_mode"] ?? "immediate"}
              onChange={(e) => setSetting("alert_email_mode", e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5"
            >
              <option value="immediate">One email per alert (immediate)</option>
              <option value="digest">One grouped digest, periodically</option>
            </select>
            {(settings["alert_email_mode"] ?? "immediate") === "digest" && (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                every
                <select
                  value={settings["alert_digest_minutes"] ?? "60"}
                  onChange={(e) => setSetting("alert_digest_minutes", e.target.value)}
                  className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">hour</option>
                  <option value="240">4 hours</option>
                  <option value="720">12 hours</option>
                  <option value="1440">day</option>
                </select>
              </label>
            )}
          </div>

          {(settings["alert_email_mode"] ?? "immediate") === "digest" && (
            <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                Still email me immediately for
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                A digest that holds everything is not safe to switch on — nobody
                accepts a server-down notice waiting an hour. These conditions skip
                the digest and email as they happen; everything else is summarised.
                Comma-separated metric names, or <code>none</code> to digest
                absolutely everything.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                {/* The measured reason this is metric-based rather than a severity
                    threshold, so the next person to touch it knows. */}
                Deliberately not "criticals only": on this fleet 86% of critical
                alerts were expiring certificates — critical, but not urgent. Keying
                on severity would put the whole flood straight back through.
              </p>
              <input
                value={
                  settings["alert_email_immediate_metrics"] ??
                  "device_offline, service_state, url_down, raid_health, backup_failed, ups_status"
                }
                onChange={(e) =>
                  setSetting("alert_email_immediate_metrics", e.target.value)
                }
                spellCheck={false}
                className="mt-2 w-full text-sm font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              Email only
              <select
                value={settings["alert_email_min_severity"] ?? "warning"}
                onChange={(e) => setSetting("alert_email_min_severity", e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5"
              >
                <option value="info">everything (info and up)</option>
                <option value="warning">warnings and criticals</option>
                <option value="critical">criticals only</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              Re-announce the same issue after
              <select
                value={settings["alert_email_renotify_hours"] ?? "24"}
                onChange={(e) => setSetting("alert_email_renotify_hours", e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5"
              >
                <option value="0">always (no suppression)</option>
                <option value="4">4 hours</option>
                <option value="12">12 hours</option>
                <option value="24">24 hours</option>
                <option value="72">3 days</option>
                <option value="168">a week</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={(settings["alert_digest_ai_summary"] ?? "true") !== "false"}
                onChange={(e) =>
                  setSetting("alert_digest_ai_summary", e.target.checked ? "true" : "false")
                }
              />
              AI summary at the top of the digest
            </label>
          </div>
          {/* These two numbers are where the volume actually comes from. Measured
              on this appliance over one week: 1,300 alerts raised, of which 563
              were info-severity memory alerts on 43 workstations (~13 each) and
              436 were certificate warnings for 212 certs re-firing ~5 times
              each. Neither is 1,300 problems. */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Info-severity alerts are usually normal behaviour (a server using its memory), and
            the same condition re-detected every cycle is still one condition. Both stay visible
            on the dashboard either way — these settings only control what is worth an email.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            In digest mode nothing is emailed as it happens — one message per interval lists
            everything new, grouped by customer and device. Alerts you have already acknowledged,
            and ones that resolved themselves before the digest ran, are left out entirely.
            <br />
            Either way, related alerts on the same device are collapsed to one: a filling disk
            raises usage, I/O-latency and SMART-health alerts, and you hear about it once.
          </p>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Where alert notifications are <strong>delivered</strong>. This is the recipient, not the
          sending identity &mdash; see below for what the alert is sent from.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={settings["alert_email"] ?? ""}
            onChange={(e) => setSettings((s) => ({ ...s, alert_email: e.target.value }))}
            placeholder="you@yourcompany.com"
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm w-72 dark:bg-gray-900"
          />
          <button
            onClick={() => setSetting("alert_email", settings["alert_email"] ?? "")}
            className="bg-brand text-white text-xs px-3 py-1.5 rounded-lg"
          >
            Save
          </button>
        </div>

        {/* How alert mail is actually addressed. Read-only on purpose: these are
            derived from the deployment name in HelpDesk setup, not set here.
            Previously this screen showed only the recipient, which left the
            impression that alert_email was also the sending address - it never
            was, and it is now genuinely two different things. */}
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
            How alert email is addressed
          </p>
          {mailIdent ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
              <div>
                Sent from <span className="font-mono">{mailIdent.alerts_address || "—"}</span>
                {mailIdent.sending_direct && (
                  <span className="text-gray-400"> — sent by this appliance directly</span>
                )}
              </div>
              <div>
                Replies go to{" "}
                {mailIdent.receiver_listening && mailIdent.helpdesk_address ? (
                  <>
                    <span className="font-mono">{mailIdent.helpdesk_address}</span> and become a
                    ticket
                  </>
                ) : (
                  <>
                    the sending account &mdash; replies do not become tickets until inbound mail is
                    set up
                  </>
                )}
              </div>
              <div className="pt-1">
                <Link to="/helpdesk/setup" className="text-blue-600 hover:underline">
                  {mailIdent.receiver_listening
                    ? "HelpDesk setup"
                    : "Set up inbound mail so replies become tickets"}
                </Link>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400">…</p>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm pt-2 border-t border-gray-100 dark:border-gray-700 mt-2">
          <input
            type="checkbox"
            checked={isOn("cert_alert_include_self_signed")}
            onChange={(e) =>
              setSetting("cert_alert_include_self_signed", e.target.checked ? "true" : "false")
            }
          />
          <span>
            Alert on self-signed certificates
            <span className="text-xs text-gray-400 ml-1">
              (off by default — avoids noise from auto-generated RDP/WinRM machine certs; the
              Certificates view still shows them)
            </span>
          </span>
        </label>
      </div>

      {/* Thresholds */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Alert thresholds
          </h2>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400">Customer</span>
              <select
                value={scopeCustomer}
                onChange={(e) => {
                  setScopeCustomer(e.target.value);
                  reloadScope(e.target.value, scope);
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
              >
                <option value="">All customers (global)</option>
                {customers.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm flex items-center gap-2">
              <span className="text-gray-500 dark:text-gray-400">Device type</span>
              <select
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value);
                  reloadScope(scopeCustomer, e.target.value);
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
              >
                <optgroup label="Agent endpoints">
                  {THRESHOLD_SCOPES.map((s) => (
                    <option key={s.v} value={s.v}>
                      {s.l}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Network devices">
                  {NETWORK_KIND_SCOPES.map((s) => (
                    <option key={s.v} value={s.v}>
                      {s.l}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
          </div>
        </div>
        {scopeUrls(scopeCustomer, scope).resettable && (
          <p className="text-xs text-gray-400 mb-3">
            Overrides the inherited values for this scope. Metrics without an override (no “reset”
            link) inherit the shown value.
          </p>
        )}
        <div className="space-y-3">
          {(NETWORK_KINDS.has(scope)
            ? [NETWORK_STATUS_METRIC]
            : order.filter((m) => m !== NETWORK_STATUS_METRIC)
          ).map((metric) => {
            const d = drafts[metric];
            if (!d) return null;
            const binary = BINARY_METRICS.has(metric);
            const timer = TIMER_METRICS.has(metric);
            const count = COUNT_METRICS.has(metric);
            const overridden = overrideSet.has(metric);
            return (
              <div
                key={metric}
                className="border border-gray-100 dark:border-gray-700 rounded-lg p-3 flex flex-wrap items-end gap-3"
              >
                <div className="w-40 text-sm font-medium text-gray-800 dark:text-gray-100">
                  {METRIC_LABELS[metric] || metric}
                  {scopeUrls(scopeCustomer, scope).resettable && overridden && (
                    <span className="ml-1 text-[10px] uppercase text-violet-600 dark:text-violet-400">override</span>
                  )}
                </div>
                {timer ? (
                  <Num
                    label="Alert after offline (sec)"
                    value={Number(d.delay_seconds ?? 0)}
                    onChange={(v) => patch(metric, "delay_seconds", v)}
                  />
                ) : count ? (
                  <Num
                    label="Failed polls before alert"
                    value={Number(d.delay_seconds ?? 0)}
                    onChange={(v) => patch(metric, "delay_seconds", v)}
                  />
                ) : binary ? (
                  <div className="text-xs text-gray-400 flex-1">
                    Categorical state — no numeric threshold; only re-alert cadence applies.
                  </div>
                ) : (
                  <>
                    <Num
                      label="Info"
                      value={d.info_pct}
                      onChange={(v) => patch(metric, "info_pct", v)}
                    />
                    <Num
                      label="Warning"
                      value={d.warn_pct}
                      onChange={(v) => patch(metric, "warn_pct", v)}
                    />
                    <Num
                      label="Critical"
                      value={d.critical_pct}
                      onChange={(v) => patch(metric, "critical_pct", v)}
                    />
                  </>
                )}
                <Num
                  label="Re-alert (hrs)"
                  value={d.re_alert_hours}
                  onChange={(v) => patch(metric, "re_alert_hours", v)}
                />
                <button
                  onClick={() => saveThreshold(metric)}
                  disabled={savingMetric === metric}
                  className="px-3 py-1.5 rounded-lg text-sm bg-brand text-white disabled:opacity-50"
                >
                  {savingMetric === metric ? "Saving…" : "Save"}
                </button>
                {scopeUrls(scopeCustomer, scope).resettable && overridden && (
                  <button
                    onClick={() => resetThreshold(metric)}
                    disabled={savingMetric === metric}
                    className="px-2 py-1.5 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
                  >
                    reset
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-device overrides */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Per-device overrides
          </h2>
          <label className="text-sm flex items-center gap-2">
            <span className="text-gray-500 dark:text-gray-400">Device</span>
            <select
              value={selectedDevice ?? ""}
              onChange={(e) => setSelectedDevice(e.target.value ? Number(e.target.value) : null)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900 max-w-xs"
            >
              <option value="">Select a device…</option>
              {devices.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.hostname}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Set the role, offline-alert delay, per-metric mutes and maintenance for a single device —
          the same controls as the device's own Alerting &amp; Maintenance tab.
        </p>
        {selectedDevice ? (
          <DeviceAlertingPanel deviceId={selectedDevice} />
        ) : (
          <div className="text-sm text-gray-400 py-4">Pick a device to edit its alert overrides.</div>
        )}
      </div>
    </div>
  );
}
